import { useTranslation } from "react-i18next";
import { useToast } from "../toast/toastContext";
import styles from "./CopyEmailButton.module.css";

/**
 * A small icon button that copies a person's full email address to the clipboard, so it can be pasted into a message or a search. The
 * outcome is a toast. It sits beside a name inside a row that opens on click, so it keeps the click to itself.
 *
 * @param props.email - The person's full email address.
 */
export function CopyEmailButton({
  email,
}: {
  email: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  const copy = async (event: React.MouseEvent): Promise<void> => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      toast.show(t("myRequests.decision.copied", { email }), "success");
    } catch {
      toast.show(t("myRequests.decision.copyFailed"), "error");
    }
  };

  return (
    <button
      type="button"
      className={styles.copy}
      aria-label={t("myRequests.decision.copy", { email })}
      onClick={(event) => void copy(event)}
    >
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15V6a2 2 0 0 1 2-2h9" />
      </svg>
    </button>
  );
}
